import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Search, ShoppingBag, Palette, Wand2 } from 'lucide-react';

const LOADING_STEPS = [
  { text: "Analyzing your saved items", detail: "Understanding what you love", icon: Sparkles, emoji: "✨" },
  { text: "Learning your style DNA", detail: "Colors, brands & aesthetics", icon: Palette, emoji: "🧬" },
  { text: "Scanning trending pieces", detail: "What's hot right now", icon: Search, emoji: "🔥" },
  { text: "Searching the best stores", detail: "Finding deals across 500+ retailers", icon: ShoppingBag, emoji: "🛍️" },
  { text: "Curating your picks", detail: "Hand-picking just for you", icon: Wand2, emoji: "💫" },
];

const STEP_DURATION = 3000;
const TOTAL_DURATION = STEP_DURATION * LOADING_STEPS.length;

export function ForYouSkeleton() {
  const [stepIndex, setStepIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const startTime = useRef(Date.now());

  useEffect(() => {
    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime.current;
      const raw = Math.min(elapsed / TOTAL_DURATION, 1);
      // Ease-out curve so it feels snappy at first, then slows
      const eased = 1 - Math.pow(1 - raw, 2.5);
      setProgress(eased * 100);

      const newStep = Math.min(Math.floor(elapsed / STEP_DURATION), LOADING_STEPS.length - 1);
      setStepIndex(newStep);
    }, 50);
    return () => clearInterval(interval);
  }, []);

  const currentStep = LOADING_STEPS[stepIndex];
  const StepIcon = currentStep.icon;

  return (
    <div className="flex flex-col items-center justify-center py-16 px-6">
      {/* Animated icon */}
      <motion.div
        className="relative w-20 h-20 mb-8"
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.4 }}
      >
        {/* Rotating ring */}
        <motion.div
          className="absolute inset-0 rounded-full border-2 border-primary/20"
          style={{ borderTopColor: 'hsl(var(--primary))' }}
          animate={{ rotate: 360 }}
          transition={{ duration: 1.2, repeat: Infinity, ease: 'linear' }}
        />
        {/* Inner glow */}
        <div className="absolute inset-1 rounded-full bg-gradient-to-br from-primary/15 to-primary/5" />
        {/* Icon */}
        <AnimatePresence mode="wait">
          <motion.div
            key={stepIndex}
            className="absolute inset-0 flex items-center justify-center"
            initial={{ scale: 0.5, opacity: 0, rotate: -20 }}
            animate={{ scale: 1, opacity: 1, rotate: 0 }}
            exit={{ scale: 0.5, opacity: 0, rotate: 20 }}
            transition={{ duration: 0.3 }}
          >
            <StepIcon className="w-8 h-8 text-primary" />
          </motion.div>
        </AnimatePresence>
      </motion.div>

      {/* Step text */}
      <AnimatePresence mode="wait">
        <motion.div
          key={stepIndex}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -12 }}
          transition={{ duration: 0.25 }}
          className="text-center mb-6"
        >
          <p className="text-lg font-semibold text-foreground flex items-center justify-center gap-2">
            <span>{currentStep.emoji}</span>
            {currentStep.text}
          </p>
          <p className="text-sm text-muted-foreground mt-1">
            {currentStep.detail}
          </p>
        </motion.div>
      </AnimatePresence>

      {/* Progress bar */}
      <div className="w-full max-w-xs">
        <div className="h-1.5 bg-muted rounded-full overflow-hidden">
          <motion.div
            className="h-full rounded-full bg-gradient-to-r from-primary/80 to-primary"
            style={{ width: `${progress}%` }}
            transition={{ duration: 0.1 }}
          />
        </div>
        {/* Step indicators */}
        <div className="flex justify-between mt-3">
          {LOADING_STEPS.map((step, idx) => (
            <motion.div
              key={idx}
              className={`w-2 h-2 rounded-full ${
                idx <= stepIndex ? 'bg-primary' : 'bg-muted-foreground/20'
              }`}
              animate={idx === stepIndex ? { scale: [1, 1.4, 1] } : {}}
              transition={{ duration: 0.5 }}
            />
          ))}
        </div>
      </div>

      {/* Skeleton preview cards to hint at what's coming */}
      <div className="w-full max-w-xs mt-8 grid grid-cols-3 gap-2">
        {[0, 1, 2].map((i) => (
          <motion.div
            key={i}
            className="aspect-square rounded-xl bg-muted/50"
            animate={{ opacity: [0.3, 0.6, 0.3] }}
            transition={{ duration: 1.5, repeat: Infinity, delay: i * 0.3 }}
          />
        ))}
      </div>
    </div>
  );
}
